import React, { useState } from "react";

function HowToUse() {
  const [activeSection, setActiveSection] = useState("overview");
  const [activeSubsection, setActiveSubsection] = useState("");

  const sections = {
    overview: {
      title: "Overview",
      content: (
        <div>
          <p>
            Welcome to Puck Stats! This comprehensive user manual will help you
            master every aspect of our stats tracking app.
          </p>
          <img
            src="/livetracker.svg"
            alt="Live Trackers"
            className="screenshot"
          />
          <p>
            Navigate through the sections using the menu to learn about app
            settings, team management, player setup, game configuration, live
            tracking, and upcoming cloud storage features.
          </p>
        </div>
      ),
    },
    homeScreen: {
      title: "Home Screen",
      content: (
        <div>
          <div className="instruction-step" id="preferences">
            <p>
              The home screen provides an list of your teams. The list is sorted
              by season with the newest on top. You can use the settings button
              on the top left to change app settings. The "+" plus button on the
              top left can be used to add new teams. You can swipe a team to the
              left to delete it.
            </p>
            <img
              src="/home-screen.svg"
              alt="Home Screen"
              className="screenshot"
            />
          </div>
        </div>
      ),
    },
    appSettings: {
      title: "App Settings",
      content: (
        <div>
          <div className="instruction-step" id="preferences">
            <ul>
              <li>
                You can choose how the live tracker handles shots when you
                change periods.
                <ul>
                  <li>Option 1: Always clear the rink surface</li>
                  <li>Option 2: Ask before clearing</li>
                  <li>Option 3: Keep shots on rink</li>
                </ul>
              </li>
              <li>
                By default the live tracker screen will keep the device screen
                awake. You can turn this feature off.
              </li>
            </ul>
            <img src="/settings.svg" alt="Settings" className="screenshot" />
          </div>
        </div>
      ),
    },
    teamSetup: {
      title: "Team Setup",
      subsections: {
        creation: "Creating Teams",
        edit: "Editing Team Details",
        roster: "Managing Rosters",
        lines: "Line Combinations",
        games: "Managing Games",
        settings: "Game Settings",
      },
      content: (
        <div>
          <div className="instruction-step" id="creation">
            <h3>Creating Teams</h3>
            <ul>
              <li>From the home screen click the "+" button.</li>
              <li>
                Enter team information:
                <ul>
                  <li>Team Name: Choose a unique name for your team</li>
                  <li>
                    League: Select the league your team belongs to (example:
                    MAHA)
                  </li>
                  <li>
                    Division: Select the division your team belongs to (example:
                    12u AA)
                  </li>
                  <li>Season: Set the season year (example: 2025/2026)</li>
                  <li>
                    Team Colors:
                    <ul>
                      <li>
                        Primary Color (first color-box): Select your main team
                        color, used for backgrounds on elements and shot
                        indicators
                      </li>
                      <li>
                        Secondary Color (second color-box): Choose a
                        complementary color, used for text on top of primary
                        color
                      </li>
                      <li>
                        Accent Color (third color-box): Pick an accent color for
                        highlights and details
                      </li>
                    </ul>
                  </li>
                  <li>Team Logo: Upload a logo image from your device</li>
                </ul>
              </li>
            </ul>
            <img src="/new-team.svg" alt="New Team" className="screenshot" />
          </div>
          <div className="instruction-step" id="edit">
            <h3>Editing Team Details</h3>
            <ul>
              <li>
                After a team has been created, you can edit its details by
                selecting the team from the home screen. This will open the team
                details screen. You can the click on any of the info at the tope
                to edit it.
              </li>
              <li>
                The export icon at the top right will change to a save icon when
                there are changes to save.
              </li>
            </ul>
            <img src="/edit-team.svg" alt="Edit Team" className="screenshot" />
          </div>
          <div className="instruction-step" id="roster">
            <h3>Managing Rosters</h3>
            <ul>
              <li>
                Add players individually or import from CSV. This can be done
                from the players tab on the team view.
              </li>
            </ul>
            <img src="/roster.svg" alt="Roster" className="screenshot" />
          </div>
          <div className="instruction-step" id="lines">
            <h3>Line Combinations</h3>
            <ul>
              <li>
                Set up lines:
                <ul>
                  <li>
                    You can set up forwards lines and defensive pairings. Just
                    simple tap the box on the rink and select a player.
                  </li>
                  <li>
                    You can name the lines by selecting the line you want from
                    the list and then clicking the gray textbox above the rink.
                  </li>
                  <li>
                    Add new lines by using the "+" button at the top left. To
                    save the lines click the "✓" at the top right.
                  </li>
                </ul>
              </li>
            </ul>
            <img
              src="/edit-lines.svg"
              alt="Edit Lines"
              className="screenshot"
            />
          </div>
          <div className="instruction-step" id="games">
            <h3>Managing Games</h3>
            <ul>
              <li>
                Set up games:
                <ul>
                  <li>
                    You can create a new game by clicking the "+" button on the
                    games tab for the team.
                  </li>
                  <li>
                    You can add a new single game or import a list of games.
                  </li>
                </ul>
              </li>
            </ul>
            <img src="/games.svg" alt="Edit Games" className="screenshot" />
          </div>
          <div className="instruction-step" id="settings">
            <h3>Game Settings</h3>
            <ul>
              <li>
                Configure game settings:
                <ul>
                  <li>
                    From the games tab on the team view, push the options icon
                    on the top left of the screen.
                  </li>
                  <li>
                    You can set the game length, which is used to calculate the
                    goalies GAA.
                  </li>
                  <li>You can also set the penalty length.</li>
                </ul>
              </li>
            </ul>
            <img
              src="/game-options.svg"
              alt="Game Settings"
              className="screenshot"
            />
          </div>
        </div>
      ),
    },
    gameSetup: {
      title: "Game Setup",
      subsections: {
        gameCreation: "Creating Games",
        gameEdit: "Before You Start Tracking",
      },
      content: (
        <div>
          <div className="instruction-step" id="gameCreation">
            <h3>Creating Games</h3>
            <ul>
              <li>
                From the games tab click on the team press the "+" button.
              </li>
              <li>
                Enter game information:
                <ul>
                  <li>Date and Time</li>
                  <li>Location</li>
                  <li>Set Home Game</li>
                  <li>
                    Select an opponent or create a new one. (opponent color will
                    be used for shot markers)
                  </li>
                </ul>
              </li>
            </ul>
            <img
              src="/game-details.svg"
              alt="New Game"
              className="screenshot"
            />
          </div>
          <div className="instruction-step" id="gameEdit">
            <h3>Before you start tracking</h3>
            <ul>
              <li>
                Before you start the live tracking remember to set your starting
                goalie and the lineup for the game. The edit lineup button is
                located at the bottom left of the screen.
              </li>
            </ul>
            <img
              src="/game-ready.svg"
              alt="Game Ready"
              className="screenshot"
            />
          </div>
          <div className="instruction-step" id="roster">
            <h3>Managing Rosters</h3>
            <ul>
              <li>
                Add players individually or import from CSV. This can be done
                from the players tab on the team view.
              </li>
            </ul>
            <img src="/roster.svg" alt="Roster" className="screenshot" />
          </div>
          <div className="instruction-step" id="lines">
            <h3>Line Combinations</h3>
            <ul>
              <li>
                Set up lines:
                <ul>
                  <li>
                    You can set up forwards lines and defensive pairings. Just
                    simple tap the box on the rink and select a player.
                  </li>
                  <li>
                    You can name the lines by selecting the line you want from
                    the list and then clicking the gray textbox above the rink.
                  </li>
                  <li>
                    Add new lines by using the "+" button at the top left. To
                    save the lines click the "✓" at the top right.
                  </li>
                </ul>
              </li>
            </ul>
            <img
              src="/edit-lines.svg"
              alt="Edit Lines"
              className="screenshot"
            />
          </div>
          <div className="instruction-step" id="games">
            <h3>Managing Games</h3>
            <ul>
              <li>
                Set up games:
                <ul>
                  <li>
                    You can create a new game by clicking the "+" button on the
                    games tab for the team.
                  </li>
                  <li>
                    You can add a new single game or import a list of games.
                  </li>
                </ul>
              </li>
            </ul>
            <img src="/games.svg" alt="Edit Games" className="screenshot" />
          </div>
          <div className="instruction-step" id="settings">
            <h3>Game Settings</h3>
            <ul>
              <li>
                Configure game settings:
                <ul>
                  <li>
                    From the games tab on the team view, push the options icon
                    on the top left of the screen.
                  </li>
                  <li>
                    You can set the game length, which is used to calculate the
                    goalies GAA.
                  </li>
                  <li>You can also set the penalty length.</li>
                </ul>
              </li>
            </ul>
            <img
              src="/game-options.svg"
              alt="Game Settings"
              className="screenshot"
            />
          </div>
        </div>
      ),
    },
    liveTracker: {
      title: "Live Tracker",
      subsections: {
        interface: "Interface Overview",
        playerArea: "Player Area",
        rink: "Live Rink",
        scoreBoard: "Scoreboard",
      },
      content: (
        <div>
          <div className="instruction-step" id="interface">
            <h3>Interface Overview</h3>
            <ul>
              <li>
                The live tracker is separated into 3 areas.
                <ul>
                  <li>Player Area (top bar)</li>
                  <li>Live Rink (middle section)</li>
                  <li>Scoreboard (bottom section)</li>
                </ul>
              </li>
            </ul>
            <img
              src="/livetracker.svg"
              alt="Live Tracker"
              className="screenshot"
            />
          </div>
          <div className="instruction-step" id="playerArea">
            <h3>Player Area</h3>
            <ul>
              <li>Tap on a player box to bring up the player menu</li>
              <li>Press and hold on a player box to change that player</li>
              <li>Swipe up in the player area to change the line</li>
              <li>
                If a player box already has a player assigned you can tap them
                to bring up a menu for penalties and face-offs
              </li>
            </ul>
            <img
              src="/player-menu.svg"
              alt="Player Menu"
              className="screenshot"
            />
          </div>
          <div className="instruction-step" id="rink">
            <h3>Live Rink</h3>
            <ul>
              <li>
                Tap on the rink to record a shot (brings up player selection for
                your team)
              </li>
              <li>
                Press and hold on the rink to record a goal (brings up player
                selection for your team)
              </li>
              <li>Goals for the other team give all players on ice a minus</li>
              <li>Tap existing shots or goals to bring up the shot menu</li>
              <li>Pinch the rink to rotate the rink to switch ends</li>
            </ul>
          </div>
          <div className="instruction-step" id="scoreBoard">
            <h3>Scoreboard</h3>
            <ul>
              <li>Displays current score and shots</li>
              <li>
                Add PPO (Power Play Opportunities) with the plus minus buttons
                below the shots.
              </li>
              <li>
                You can edit and delete penalties with the list right above the
                done button.
              </li>
            </ul>
          </div>
        </div>
      ),
    },
    cloudStorage: {
      title: "Cloud Storage",
      subsections: {},
      content: (
        <div>
          <div className="instruction-step" id="setup">
            <h3>Coming Soon</h3>
          </div>
          <p>Stay tuned for updates on when this feature will be available!</p>
        </div>
      ),
    },
  };

  const handleSectionChange = (sectionKey) => {
    setActiveSection(sectionKey);
    setActiveSubsection("");
  };

  const handleSubsectionChange = (subsectionKey) => {
    setActiveSubsection(subsectionKey);

    // Scroll to the subsection with smooth behavior
    const element = document.getElementById(subsectionKey);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
    }
  };

  return (
    <div className="page-container manual-page-container">
      <h1 className="page-title">Puck Stats User Manual</h1>

      <div className="manual-layout">
        <nav className="manual-nav">
          <div className="nav-section">
            <h3>Sections</h3>
            <ul className="nav-menu">
              {Object.entries(sections).map(([key, section]) => (
                <li key={key}>
                  <button
                    className={`nav-button ${
                      activeSection === key ? "active" : ""
                    }`}
                    onClick={() => handleSectionChange(key)}
                  >
                    {section.title}
                  </button>
                  {activeSection === key && section.subsections && (
                    <ul className="nav-submenu">
                      {Object.entries(section.subsections).map(
                        ([subKey, subTitle]) => (
                          <li key={subKey}>
                            <button
                              className={`nav-subbutton ${
                                activeSubsection === subKey ? "active" : ""
                              }`}
                              onClick={() => handleSubsectionChange(subKey)}
                            >
                              {subTitle}
                            </button>
                          </li>
                        )
                      )}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </nav>

        <main className="manual-content">
          <h2 className="content-title">{sections[activeSection].title}</h2>
          <div className="page-content">{sections[activeSection].content}</div>
        </main>
      </div>

      <div className="help-section">
        <h2 className="page-subtitle">Need More Help?</h2>
        <p>
          If you have questions that aren't covered in this manual, or if you
          encounter technical issues, don't hesitate to reach out for support at{" "}
          <a href="mailto:info@puckstatsapp.com">info@puckstatsapp.com</a>.
          We're here to help you make the most of your Puck Stats experience!
        </p>
      </div>
    </div>
  );
}

export default HowToUse;
